import React, { useEffect, useMemo, useState } from "react";
import { Modal, SmoothText } from "../../../SharedComponents";
import { ModalNames } from "../../../../Constants/modalNames";
import { useLanguage } from "../../../../Store/modules/language";
import settings from "../../../../settings/settings";
import { useTheme } from "../../../../Store/modules/theme";
import Logo from "../../../Icons/logo_small2X.png";
import { Div, Icon, Text } from "../../../Kit";
import { ArrowLeft, ArrowRight, RefreshCircle } from "iconsax-react";
import { useModal } from "../../../../Store/modules/modal/modal";
import { useGetResultModalData } from "../../../../Store/modules/ai/getResultModalData/getResultModalData";
import { colors } from "../../../../StyleSheet";
import { Button } from "../../../Kit/Button";
import { useUserServices } from "../../../../Store/modules/subService/userServices";
import Loader from "react-spinners/PropagateLoader";
import { SegmentNextPage } from "../../../../Constants/SegmentNextPage";
import { useUploadedFiles } from "../../../../Store/modules/files/uploadedFiles/uploadedFiles";
import { removeHtmlEscaeWrapper } from "../../../../Utils/stringProcessor";

export const ResultModal: React.FC = () => {
  const { direction, locale } = useLanguage();
  const { name: themeName } = useTheme();
  const { dispatchCloseModal, modalProps, name } = useModal();
  const [reloadLoading, setReloadLoading] = useState(false);
  const { dispatchResetFile } = useUploadedFiles();

  const {
    segment_id,
    sub_service_id,
    user_service_id,
    segment,
    router,
    goToNextSegment,
    service_id,
  } = modalProps;

  const isOpen = useMemo(() => name === ModalNames.Result, [name]);

  const { getResultModalDataLoading, getResultModalData, dispatchGetResultModalData } =
    useGetResultModalData();

  const { userServiceId } = useUserServices({
    serviceId: Number(service_id),
  });

  const { user_session, short_info } = getResultModalData?.data || {};

  useEffect(() => {
    if (isOpen) {
      dispatchGetResultModalData({
        segment_id,
        sub_service_id,
        user_service_id,
      });
    }
  }, [dispatchGetResultModalData, segment_id, sub_service_id, user_service_id, isOpen]);

  const onContinue = () => {
    if (goToNextSegment) {
      dispatchCloseModal();
      return goToNextSegment();
    }
    if (segment?.next_step === SegmentNextPage.PurchaseModels) {
      dispatchCloseModal();
      return router(`/submit-process/final-analysis-test/${service_id}`);
    } else {
      dispatchCloseModal();
      return router(
        `/result/${userServiceId}${sub_service_id ? "?sub-service=" + sub_service_id : ""}`,
      );
    }
  };

  return (
    <Modal
      modalName={ModalNames.Result}
      modalClassName="w-[80%] xs:w-[90%] md:h-[80%] xs:h-[90%] rounded-[5px] relative flex flex-col"
      preventToClose
    >
      <div dir={direction} className="flex flex-col h-full pt-[15px] md:px-[40px]">
        {/* Mobile reload icon */}
        <Icon
          Component={RefreshCircle}
          size={28}
          color="primary"
          onClick={() => {
            if (!getResultModalDataLoading) {
              dispatchResetFile();
              setReloadLoading(true);
              window.location.reload();
            }
          }}
          className={`xs:inline md:hidden absolute left-[20px] top-[38px] ${
            getResultModalDataLoading ? "not-allowed opacity-40" : "opacity-100 pointer"
          }`}
        />

        {/* Header */}
        <div className="flex items-center xs:flex-col md:flex-row justify-between shrink-0">
          <div
            className="flex items-center xs:justify-start xs:flex-col md:flex-row"
            dir={direction}
          >
            <div className="flex items-center">
              <img className="w-[40px]" src={settings.logo?.[themeName] ?? Logo} alt="logo" />
            </div>
            {short_info?.map((info, index) => (
              <Text
                elementType="span"
                key={index}
                className={`${
                  index === 0
                    ? "md:text-[24px] xs:text-[24px] mx-[20px] xs:mt-2 md:mt-0"
                    : "text-[14px] mt-1.5 md:mx-3"
                }`}
                fontWeight={400}
              >
                {info}
              </Text>
            ))}
          </div>

          {/* Desktop reload button */}
          <Div
            className={`group h-[41px] px-2 justify-center items-center rounded-[5px] ${
              getResultModalDataLoading ? "not-allowed opacity-40" : "opacity-100 pointer"
            } xs:hidden md:flex transition-all duration-300`}
            borderColor="primary"
            borderStyle="solid"
            borderWidth="1px"
            dir={direction}
            onClick={() => {
              if (!getResultModalDataLoading) {
                setReloadLoading(true);
                window.location.reload();
              }
            }}
          >
            <Text
              elementType="span"
              className="mb-0.5 text-base opacity-0 w-0 overflow-hidden group-hover:ml-2 group-hover:opacity-100 group-hover:w-auto transition-all duration-300"
              color="primary"
            >
              reload
            </Text>
            <Icon Component={RefreshCircle} size={20} color="primary" />
          </Div>
        </div>

        {/* Body */}
        {getResultModalDataLoading || reloadLoading ? (
          <div className="flex flex-col justify-center items-center flex-1 w-full">
            <Loader size={24} color={colors.primary} />
            <Text elementType="p" color="textNotActive" className="text-base mt-[64px] text-center">
              result.waitingForProcess
            </Text>
          </div>
        ) : (
          <Div
            bg="secondary"
            dir={direction}
            className="mt-[24px] py-[24px] md:px-[35px] xs:px-[25px] w-full rounded-[5px] xs:h-[calc(100%-10px)]  md:h-[53vh] overflow-y-auto hide-scrollbar"
          >
            <SmoothText text={removeHtmlEscaeWrapper(user_session?.res_html || "")} />
          </Div>
        )}
        <div className="flex justify-center items-center xs:flex-col md:flex-row xs:pb-5 md:pb-0 pt-[24px] shrink-0">
          <Button
            title="submitProcess.personalDetails.resultModal.continue"
            bg="primary"
            className="w-[214px] h-[45px] xs:mt-2 md:mt-0 md:mx-2"
            EndIconComponent={locale === "fa" ? ArrowLeft : ArrowRight}
            onClick={onContinue}
            loading={getResultModalDataLoading}
          />
        </div>
      </div>
    </Modal>
  );
};
